import type { CSSProperties } from "react";

import { Text } from "@mantine/core";
import clsx from "clsx";
import { useEffect, useState } from "react";

import styles from "./autoAvailablePill.module.css";

interface Props {
    durationMs: number;
    onTimeout: () => void;
    isVoice: boolean;
}

const TICK_MS = 1000;
// Normalizes the ring's circumference to 100 so the dash math is independent of its radius.
const RING_LENGTH = 100;

function secondsRemaining(ms: number): number {
    return Math.max(0, Math.ceil(ms / 1000));
}

export function AutoAvailableCountdown({ durationMs, onTimeout, isVoice }: Props) {
    const [remainingMs, setRemainingMs] = useState(durationMs);

    useEffect(() => {
        const deadline = Date.now() + durationMs;
        const interval = setInterval(() => {
            const remaining = deadline - Date.now();
            if (remaining <= 0) {
                clearInterval(interval);
                setRemainingMs(0);
                onTimeout();
                return;
            }
            setRemainingMs(remaining);
        }, TICK_MS);
        return () => clearInterval(interval);
    }, [durationMs, onTimeout]);

    const seconds = secondsRemaining(remainingMs);
    const fractionRemaining = Math.max(0, Math.min(1, remainingMs / durationMs));
    const workNoun = isVoice ? "calls" : "chats";

    return (
        <>
            <CountdownRing durationMs={durationMs} fractionRemaining={fractionRemaining} />
            <Text
                component="span"
                role="timer"
                aria-label={`You'll be available for ${workNoun} in ${seconds} seconds`}
                className={styles.countdownText}
            >
                You&apos;ll be available for {workNoun} in {seconds}s
            </Text>
        </>
    );
}

interface CountdownRingProps {
    durationMs: number;
    fractionRemaining: number;
}

function CountdownRing({ durationMs, fractionRemaining }: CountdownRingProps) {
    return (
        <svg className={styles.countdownRing} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <circle className={styles.countdownRingTrack} cx="8" cy="8" r="6.5" pathLength={RING_LENGTH} />
            <circle
                className={clsx(styles.countdownRingArc, styles.countdownRingArcAnimated)}
                cx="8"
                cy="8"
                r="6.5"
                pathLength={RING_LENGTH}
                style={
                    {
                        "--aa-offset": String(RING_LENGTH * (1 - fractionRemaining)),
                        "--aa-duration": `${durationMs}ms`,
                    } as CSSProperties
                }
            />
        </svg>
    );
}
